import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ScoreDial } from "../components/ScoreDial";
import { api } from "../lib/api";
import { bandColor } from "../lib/bands";
import { CheckCircle2, AlertTriangle, Eye, Target, ArrowUpRight } from "lucide-react";

const SUB = [
  { key: "personal", label: "Personal Stability", color: "#DDA15E" },
  { key: "professional", label: "Professional Momentum", color: "#FF8A00" },
  { key: "financial", label: "Financial Health", color: "#5C6B4E" },
];

function InsightList({ icon: Icon, title, items, color }) {
  return (
    <div className="card-soft p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} style={{ color }} />
        <h3 className="font-display font-bold text-ink">{title}</h3>
      </div>
      <ul className="space-y-2.5">
        {(items || []).map((it, i) => (
          <li key={i} className="text-sm text-stone leading-relaxed flex gap-2">
            <span style={{ color }}>—</span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/assessments/${shareId}`).then(({ data }) => setData(data)).catch(() => setError("This shared report is unavailable."));
  }, [shareId]);

  if (error) return <div className="min-h-screen grain flex items-center justify-center text-stone">{error}</div>;
  if (!data) return <div className="min-h-screen grain flex items-center justify-center text-stone font-mono text-sm">Loading shared report…</div>;

  const { scores, fit, insight, owner_first_name } = data;
  const fitColor = bandColor(fit.fit_band);

  return (
    <div className="min-h-screen grain">
      <header className="bg-oat/80 backdrop-blur-xl border-b border-clay">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/scorecaster-logo.png" alt="Scorecaster" className="h-10 w-auto" />
            <span className="font-display font-extrabold text-xl text-ink hidden sm:block">Scorecaster</span>
          </Link>
          <Link to="/auth?mode=register" data-testid="share-cta" className="px-5 py-2 text-sm font-semibold rounded-full bg-brand-red text-white hover:bg-[#e63f3f] transition-colors duration-300">
            Get your score
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 relative z-10">
        <span className="label-tiny">Shared Scorecaster report</span>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2 mb-10 text-ink">
          {owner_first_name}'s readiness for {fit.decision_label}.
        </h1>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="card-soft p-8 flex flex-col items-center justify-center">
            <ScoreDial score={scores.core} band={scores.band} />
            <p className="text-sm text-stone text-center mt-6 max-w-xs">{scores.band_meaning}</p>
          </div>
          <div className="card-soft p-8 flex flex-col justify-center gap-6">
            <span className="label-tiny">Pillar breakdown</span>
            {SUB.map((s) => (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-ink">{s.label}</span>
                  <span className="font-mono text-sm" style={{ color: s.color }}>{scores[s.key]}</span>
                </div>
                <div className="h-2.5 bg-clay rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ background: s.color, width: `${scores[s.key]}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-clay bg-gradient-to-br from-[#FFF3E4] to-[#FDFBF7] p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="label-tiny">Decision fit</span>
              <h2 className="font-display font-bold text-xl md:text-2xl mt-2 text-ink">{fit.decision_label}</h2>
              <p className="text-sm text-stone mt-2">Estimated probability of success: <span className="font-mono" style={{ color: fitColor }}>{fit.probability}</span></p>
            </div>
            <div className="text-right">
              <div className="font-mono text-5xl font-extrabold" style={{ color: fitColor }}>{fit.fit}</div>
              <span className="label-tiny" style={{ color: fitColor }}>{fit.fit_band}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 card-soft p-8">
          <span className="label-tiny">AI read</span>
          <p className="text-base md:text-lg text-ink leading-relaxed mt-3">{insight.summary}</p>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-6">
          <InsightList icon={CheckCircle2} title="Strengths" items={insight.strengths} color="#5C6B4E" />
          <InsightList icon={AlertTriangle} title="Vulnerabilities" items={insight.vulnerabilities} color="#DDA15E" />
          <InsightList icon={Eye} title="Blind spots" items={insight.blind_spots} color="#FF4D4D" />
        </div>

        <div className="mt-12 rounded-3xl border border-clay bg-gradient-to-br from-[#FFF3E4] to-[#FDFBF7] p-10 text-center">
          <Target size={22} className="text-brand-orange mx-auto mb-3" />
          <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-ink">Score your own situation.</h2>
          <p className="text-stone mt-3 max-w-md mx-auto">Take the free 4-minute Scorecaster survey and forecast your next move.</p>
          <Link to="/auth?mode=register" className="inline-flex items-center gap-2 mt-6 px-8 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
            Get started <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}
